import {
  InvestmentPurposeAndNatureDurationType,
  InvestmentObjectiveType,
  PlannedServiceType,
} from 'api/types/entity/legal';
import {
  TypedForm,
  FormErrors as FormErrorsBase,
  ObjectSchema,
  string,
  object,
  mixed,
  array,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import { SelectType } from 'types/selectType';

export interface InvestmentPurposeAndNatureFormType {
  plannedServices: SelectType<PlannedServiceType>[];
  otherPlannedServices?: string;
  investmentDuration: SelectType<InvestmentPurposeAndNatureDurationType>;
  investmentObjectives: SelectType<InvestmentObjectiveType>[];
  otherInvestmentObjective?: string;
}

export const isOtherPlannedService = (
  values: SelectType<PlannedServiceType>[]
) => values?.some((v) => v?.value === PlannedServiceType.Other);

export const isOtherInvestmentObjective = (
  values: SelectType<InvestmentObjectiveType>[]
) => values?.some((v) => v?.value === InvestmentObjectiveType.Other);

export const getPlannedServicesOptions = (t: TFunc) =>
  Object.values(PlannedServiceType).map((v) => ({
    label: t(
      `onboarding.investmentPurposeAndNature.plannedServices.types.${v}`
    ),
    value: v,
  }));

export const getInvestmentDurationOptions = (t: TFunc) =>
  Object.values(InvestmentPurposeAndNatureDurationType).map((v) => ({
    label: t(
      `onboarding.investmentPurposeAndNature.investmentDuration.types.${v}`
    ),
    value: v,
  }));

export const getInvestmentObjectiveOptions = (t: TFunc) =>
  Object.values(InvestmentObjectiveType).map((v) => ({
    label: t(
      `onboarding.investmentPurposeAndNature.investmentObjectives.types.${v}`
    ),
    value: v,
  }));

export const getSchema = (
  t: TFunc
): ObjectSchema<InvestmentPurposeAndNatureFormType> =>
  object({
    plannedServices: array()
      .of(
        object({
          value: mixed<PlannedServiceType>()
            .oneOf(Object.values(PlannedServiceType))
            .required(),
          label: string().required(),
        })
          .default(null)
          .required()
      )
      .min(1, t('validation.required'))
      .required(),

    otherPlannedServices: string().when('plannedServices', {
      is: isOtherPlannedService,
      then: (schema) => schema.required().max(200),
    }),

    investmentDuration: object({
      value: mixed<InvestmentPurposeAndNatureDurationType>()
        .oneOf(Object.values(InvestmentPurposeAndNatureDurationType))
        .required(),
      label: string().required(),
    })
      .default(null)
      .required(),

    investmentObjectives: array()
      .of(
        object({
          value: mixed<InvestmentObjectiveType>()
            .oneOf(Object.values(InvestmentObjectiveType))
            .required(),
          label: string().required(),
        })
          .default(null)
          .required()
      )
      .min(1, t('validation.required'))
      .required(),

    otherInvestmentObjective: string().when('investmentObjectives', {
      is: isOtherInvestmentObjective,
      then: (schema) => schema.required().max(200),
    }),
  });

export const { Field, Form, useFormContext, Submit } =
  TypedForm<InvestmentPurposeAndNatureFormType>();

export type FormErrors = FormErrorsBase<InvestmentPurposeAndNatureFormType>;
